import Service from '@ember/service';
import { service } from '@ember/service';
import type StateService from './state';
import { assert } from '@ember/debug';
import BesselFilter from '../models/filters/bessel-filter';
import ButterworthFilter from '../models/filters/butterworth-filter';
import ChebyshevFilter from '../models/filters/chebyshev-filter';
import InverseChebyshevFilter from '../models/filters/inverse-chebyshev-filter';
import CircuitService from './circuit';

export interface FilterResult {
  filterAmplitude: number | null;
  filterAttenuation: number | null;
}

export default class FilterService extends Service {
  @service declare state: StateService;
  @service declare circuit: CircuitService;
  besselFilter = new BesselFilter();
  butterworth = new ButterworthFilter();
  chebyshevFilter = new ChebyshevFilter();
  inverseChebyshevFilter = new InverseChebyshevFilter();

  get filterOrder(): number {
    return this.state.filterOrder;
  }
  set filterOrder(value: number) {
    this.state.filterOrder = value;
    this.circuit.updateParams('useFilters');
  }

  get passbandRipple(): number {
    assert('Should only appear when filter topology is Chebyshev', this.filterTopology === 1);
    return this.state.passbandRipple;
  }
  set passbandRipple(value: number) {
    assert('Should only appear when filter topology is Chebyshev', this.filterTopology === 1);
    this.state.passbandRipple = value;
    this.circuit.updateParams('useFilters');
  }

  get stopbandAtten(): number {
    assert('Should only appear when filter topology is Inverse Chebyshev', this.filterTopology === 2);
    return this.state.stopbandAtten;
  }
  set stopbandAtten(value: number) {
    assert('Should only appear when filter topology is Inverse Chebyshev', this.filterTopology === 2);
    this.state.stopbandAtten = value;
    this.circuit.updateParams('useFilters');
  }

  get displayFilters(): boolean {
    return this.state.displayFilters;
  }
  set displayFilters(value: boolean) {
    this.state.displayFilters = value;
    this.circuit.updateParams('useFilters');
  }

  get filterTopology(): number {
    return this.state.filterTopology;
  }
  set filterTopology(value: number) {
    this.state.filterTopology = value;
    this.circuit.updateParams('useFilters');
  }

  get filterType(): number {
    return this.state.filterType;
  }
  set filterType(value: number) {
    this.state.filterType = value;
    this.circuit.updateParams('useFilters');
  }

  get fc(): number {
    return this.state.fc;
  }
  set fc(value: number) {
    this.state.fc = value;
    this.circuit.updateParams('useFilters');
  }

  applyFilter(frequency: number): FilterResult {
    const displayFilters = this.state.displayFilters;
    let result: FilterResult = { filterAmplitude: null, filterAttenuation: null };

    if (displayFilters) {
      if (this.filterTopology === 0) {
        result = this.butterworth.filterOne(frequency, this.state.fc, this.state.filterOrder);
      } else if (this.filterTopology === 1) {
        result = this.chebyshevFilter.filterOne(frequency, this.fc, this.filterOrder, this.passbandRipple);
      } else if (this.filterTopology === 2) {
        result = this.inverseChebyshevFilter.filterOne(frequency, this.fc, this.filterOrder, this.stopbandAtten);
      } else if (this.filterTopology === 3) {
        result = this.besselFilter.filterOne(frequency, this.fc, this.filterOrder);
      }
    }

    return result;
  }
}
declare module '@ember/service' {
  interface Registry {
    filter: FilterService;
  }
}
